import { getValidAccessToken, check401 } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";
import { canPreviewOtherVerticals } from "@utils/userUtils";

/**
 * Per-state enablement detail for a vertical. `enabled` is the AND of:
 *   • the vertical/state pair has a row in `vertical_state_support`
 *   • the aggregator reports content for that pair
 *
 * For authorized preview users `enabled` may be force-flipped to `true` server-side
 * when there is at least one `vertical_state_support` row for the pair; when
 * content is still pending in the aggregator, `preview_only_reason` carries the
 * reason it would have been greyed out for a normal user (Hub shows COMING SOON).
 */
export interface VerticalStateSupport {
  state_code: string;
  enabled: boolean;
  /**
   * Why this state/vertical pair is disabled. One of:
   * 'not_planned' | 'no_aggregator_data' | 'rollout_alpha' | 'rollout_beta' | null
   */
  disabled_reason: string | null;
  /**
   * Set only on authorized preview responses: when non-null, the row was
   * disabled for normal users but force-enabled for preview. The reason
   * mirrors the values listed for `disabled_reason`.
   */
  preview_only_reason: string | null;
  has_calendar_items: boolean;
  has_penalties: boolean;
  has_regulations: boolean;
}

/**
 * One row in the verticals catalog returned by `GET /api/v1/verticals`.
 *
 * • `enabled` is the top-level rollup: true when at least one state in the
 *   country is enabled. The signup chooser greys out the row when this is false.
 * • `lookup_mode` tells the onboarding flow which identifier to ask for next
 *   (NPI, FAA cert, CAGE/UEI, org+state, or none).
 * • `preview_only_reason` is set only for authorized preview callers when rollout
 *   content is still pending; see the per-state field above.
 */
export interface VerticalCatalogEntry {
  slug: string;
  label: string;
  lookup_mode: "npi" | "org_state" | "faa_certificate" | "cage_uei" | "none";
  description: string;
  enabled: boolean;
  disabled_reason: string | null;
  preview_only_reason: string | null;
  states: VerticalStateSupport[];
  signup_metadata?: Record<string, unknown> | null;
}

export interface VerticalsCatalogResponse {
  country_code: string;
  verticals: VerticalCatalogEntry[];
}

/**
 * Vertical context for the current authenticated user. Returned by
 * `GET /api/v1/me/vertical-context` and used by the preview UI to decide
 * whether to render the vertical preview switcher.
 */
export interface VerticalContextResponse {
  user_vertical: string | null;
  current_facility_vertical: string | null;
  effective_vertical: string;
  can_preview_other_verticals: boolean;
}

const API_BASE = getApiOrigin();
const VERTICALS_PATH = "/api/v1/verticals";
const ME_VERTICAL_CONTEXT_PATH = "/api/v1/me/vertical-context";

const VERTICAL_CONTEXT_CACHE_TTL_MS = 60_000;
let _verticalContextCache: { at: number; data: VerticalContextResponse } | null =
  null;
const _verticalContextInFlight = new Map<
  string,
  Promise<VerticalContextResponse>
>();

/** Test-only: reset vertical-context dedupe state. */
export function clearVerticalContextCacheForTests(): void {
  _verticalContextCache = null;
  _verticalContextInFlight.clear();
}

/**
 * Fetch the public verticals catalog. This endpoint is unauthenticated so it
 * can power the signup vertical chooser before the user has logged in.
 *
 * The optional `countryCode` narrows the catalog (defaults to USA on the
 * backend). Disabled verticals are still returned; the UI is expected to grey
 * them out and surface `disabled_reason`.
 */
export async function getVerticalsCatalog(
  countryCode?: string,
): Promise<VerticalsCatalogResponse> {
  if (!API_BASE) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }
  const qs = countryCode ? `?country_code=${encodeURIComponent(countryCode)}` : "";
  const url = `${API_BASE}${VERTICALS_PATH}${qs}`;
  const headers: Record<string, string> = { Accept: "application/json" };
  // Forward an access token when present so server-side feature flags can
  // include preview-only verticals; this endpoint is otherwise public.
  const token = getValidAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(url, { method: "GET", headers });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Failed to fetch verticals catalog: ${response.status}`);
  }
  return (await response.json()) as VerticalsCatalogResponse;
}

/** Read the current user's vertical context (requires auth). */
export async function getMyVerticalContext(): Promise<VerticalContextResponse> {
  if (!API_BASE) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env.",
    );
  }
  const now = Date.now();
  if (
    _verticalContextCache &&
    now - _verticalContextCache.at < VERTICAL_CONTEXT_CACHE_TTL_MS
  ) {
    return _verticalContextCache.data;
  }

  const inflightKey = "me";
  const existing = _verticalContextInFlight.get(inflightKey);
  if (existing) {
    return existing;
  }

  const requestPromise = (async (): Promise<VerticalContextResponse> => {
    const url = `${API_BASE}${ME_VERTICAL_CONTEXT_PATH}`;
    const token = getValidAccessToken();
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await fetch(url, { method: "GET", headers });
    check401(response);
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(
        text || `Failed to fetch vertical context: ${response.status}`,
      );
    }
    const data = (await response.json()) as VerticalContextResponse;
    _verticalContextCache = { at: Date.now(), data };
    return data;
  })().finally(() => {
    _verticalContextInFlight.delete(inflightKey);
  });

  _verticalContextInFlight.set(inflightKey, requestPromise);
  return requestPromise;
}

/**
 * Storage key for the transient "preview vertical" used by the authorized
 * left-nav popout. The Hub keeps it in sessionStorage and forwards it as the
 * `X-Preview-Vertical` header on outgoing API calls. It is intentionally NOT
 * persisted to the user record on the server.
 */
export const PREVIEW_VERTICAL_STORAGE_KEY = "unicomply.previewVertical";
export const PREVIEW_VERTICAL_CHANGED_EVENT = "unicomply:preview-vertical-changed";

/**
 * Landing vertical from /aviation → /login?vertical=aviation (etc.).
 * Always written on auth — not gated by canPreviewOtherVerticals — so Dashboard
 * pricing redirects never pick a cross-vertical preferred facility (e.g. Seagen
 * pharma after an aviation login).
 *
 * Aviation landing also pins API scope to aviation for any role (see
 * {@link getRequestVerticalOverride}). The V switcher stays available for
 * executive / global-admin only ({@link canPreviewOtherVerticals}).
 */
export const LANDING_VERTICAL_STORAGE_KEY = "unicomply.landingVertical";

/** Landing verticals that pin API scope for every authenticated user (no role gate). */
export const OPEN_LANDING_VERTICALS = new Set(["aviation"]);

/** Read the active preview vertical from sessionStorage. */
export function getPreviewVertical(): string | null {
  try {
    return sessionStorage.getItem(PREVIEW_VERTICAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Read landing vertical from sessionStorage (auth entry vertical). */
export function getLandingVertical(): string | null {
  try {
    return sessionStorage.getItem(LANDING_VERTICAL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * True when the session has an open landing vertical (currently aviation).
 * Non-preview users are aviation-only; executives still keep the V switcher.
 */
export function isLandingVerticalLocked(): boolean {
  const landing = getLandingVertical();
  return Boolean(landing && OPEN_LANDING_VERTICALS.has(landing));
}

/**
 * Persist landing vertical for preferred-facility pricing routing and open
 * landing locks (aviation). Unlike preview sync, this is not gated by
 * canPreviewOtherVerticals.
 *
 * Dispatches {@link PREVIEW_VERTICAL_CHANGED_EVENT} so facility UI re-scopes
 * immediately (same event the V switcher uses).
 */
export function setLandingVerticalSession(
  vertical: string | null | undefined,
): void {
  const slug =
    typeof vertical === "string" ? vertical.trim().toLowerCase() : "";
  try {
    if (slug) {
      sessionStorage.setItem(LANDING_VERTICAL_STORAGE_KEY, slug);
      // Drop stale selected facility from another vertical (e.g. UF Health
      // after /aviation login) before the dashboard mounts.
      if (OPEN_LANDING_VERTICALS.has(slug)) {
        try {
          const raw = sessionStorage.getItem("filtered_selectedFacility");
          if (raw) {
            const parsed = JSON.parse(raw) as { vertical?: string };
            const facilityVertical = (
              parsed?.vertical || "healthcare"
            )
              .toString()
              .trim()
              .toLowerCase();
            if (facilityVertical !== slug) {
              sessionStorage.removeItem("filtered_selectedFacility");
            }
          }
        } catch {
          sessionStorage.removeItem("filtered_selectedFacility");
        }
      }
    } else {
      sessionStorage.removeItem(LANDING_VERTICAL_STORAGE_KEY);
    }
  } catch {
    /* sessionStorage may be unavailable */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(PREVIEW_VERTICAL_CHANGED_EVENT, {
        detail: { slug: getRequestVerticalOverride() },
      }),
    );
  } catch {
    /* Event constructors unsupported — sessionStorage still set */
  }
}

/**
 * Vertical used to scope preferred-facility → pricing redirects.
 * Preview wins when set; else landing auth vertical.
 */
export function getVerticalScopeForPreferredRouting(): string | null {
  return getRequestVerticalOverride() || getLandingVertical();
}

/**
 * Session vertical to forward as ``X-Preview-Vertical`` / facility list scope.
 *
 * Priority:
 * 1. Executive preview switcher slug (role-gated)
 * 2. Open landing lock (aviation from /aviation login — any role)
 */
export function getRequestVerticalOverride(): string | null {
  if (canPreviewOtherVerticals()) {
    const preview = getPreviewVertical();
    if (preview) return preview;
  }
  const landing = getLandingVertical();
  if (landing && OPEN_LANDING_VERTICALS.has(landing)) {
    return landing;
  }
  return null;
}

/** Set or clear the active preview vertical. */
export function setPreviewVertical(slug: string | null): void {
  try {
    if (slug) {
      sessionStorage.setItem(PREVIEW_VERTICAL_STORAGE_KEY, slug);
    } else {
      sessionStorage.removeItem(PREVIEW_VERTICAL_STORAGE_KEY);
    }
  } catch {
    // sessionStorage may be disabled in private browsing; the preview is best-effort.
  }
}

/**
 * Apply a session preview vertical so dashboard / facility lists open on that
 * vertical immediately (landing login via ?vertical=, post-purchase, etc.).
 *
 * No-op when the user cannot preview other verticals (header would be stripped).
 * Returns true when preview was written / event dispatched.
 */
export function syncPreviewVerticalSession(
  vertical: string | null | undefined,
): boolean {
  if (!canPreviewOtherVerticals()) return false;

  const slug =
    typeof vertical === "string" ? vertical.trim().toLowerCase() : "";
  if (!slug) return false;

  const current = getPreviewVertical();
  if (current !== slug) {
    setPreviewVertical(slug);
  }
  try {
    window.dispatchEvent(
      new CustomEvent(PREVIEW_VERTICAL_CHANGED_EVENT, {
        detail: { slug },
      }),
    );
  } catch {
    /* Event constructors unsupported — sessionStorage still set */
  }
  return true;
}

/**
 * After checkout (esp. sales-bypass), sync session preview to the purchased
 * facility vertical. Same behavior as {@link syncPreviewVerticalSession}.
 */
export function syncPreviewVerticalAfterPurchase(
  facilityVertical: string | null | undefined,
): boolean {
  return syncPreviewVerticalSession(facilityVertical);
}

/** Active vertical for facility list queries (preview/landing override, then user context). */
export async function resolveActiveVertical(): Promise<string> {
  const override = getRequestVerticalOverride();
  if (override) return override;
  try {
    const context = await getMyVerticalContext();
    return context.effective_vertical || context.user_vertical || "healthcare";
  } catch {
    return "healthcare";
  }
}

/**
 * Human-friendly label for a `disabled_reason`. Used by tooltips on greyed-out
 * vertical cards so the user understands why an option is unavailable.
 */
export function describeDisabledReason(reason: string | null | undefined): string {
  switch (reason) {
    case "not_planned":
      return "Not yet planned for this region.";
    case "no_aggregator_data":
      return "Coming soon — regulatory content is being prepared.";
    case "rollout_alpha":
      return "In private alpha. Contact sales for early access.";
    case "rollout_beta":
      return "In beta. Available on request.";
    default:
      return "";
  }
}

/**
 * Tooltip copy for a `preview_only_reason` value. These rows ARE clickable
 * (the server force-enabled them for the authorized preview cohort). Copy
 * matches the COMING SOON badge — content is not fully rolled out in the
 * aggregator yet for every planned state.
 */
export function describePreviewOnlyReason(
  reason: string | null | undefined,
): string {
  switch (reason) {
    case "no_aggregator_data":
      return "Coming soon — regulatory content is still being prepared.";
    case "rollout_alpha":
      return "Coming soon — private alpha.";
    case "rollout_beta":
      return "Coming soon — private beta.";
    default:
      return "";
  }
}
